import React, { useState, useEffect } from 'react';
import { Popup, Polyline, Polygon } from 'react-leaflet';
import L from 'leaflet';
import { getLeafletColor } from '../../util/leafletHelpers';
import TractPopup from '../maps/TractPopup';
import AppMap from '../maps/AppMap';
import EditablePolygonGroup from '../maps/EditablePolygonGroup';
import DraggableMarker from '../maps/DraggableMarker';
import TractPolygonTargetDialog from './TractPolygonTargetDialog';

const WellMapCard = (props) => {
    const {
        latitude,
        longitude,
        latitudeBottom,
        longitudeBottom,
        name,
        land,
        tracts = [],
        onCoordsExtracted,
        uploadRef,
        editMode = false,
        onSurfacePositionChange,
        onBottomPositionChange,
        onTractPolygonChange,
    } = props;

    const [map, setMap] = useState(null);
    // Coords of a freshly drawn polygon awaiting a target-tract selection.
    // While set, we render the shape as a dashed preview and show the picker dialog.
    const [pendingPolygon, setPendingPolygon] = useState(null);

    const handleCoordsExtracted = (coords) => {
        const surface = { lat: coords[0][0], lng: coords[0][1] };
        const bottomHole = coords.length > 1
            ? { lat: coords[coords.length - 1][0], lng: coords[coords.length - 1][1] }
            : { ...surface };
        onCoordsExtracted(surface, bottomHole);
    };

    const tractPolygons = tracts
        .map((tract) => {
            if (!tract.geometry) return null;
            try {
                const geo = JSON.parse(tract.geometry);
                if (Array.isArray(geo) && geo.length > 0) {
                    return {
                        positions: [geo.map(([lng, lat]) => [lat, lng])],
                        color: tract.color,
                        tract,
                    };
                }
            } catch (e) {}
            return null;
        })
        .filter(Boolean);

    // Fit map to include the wellbore plus every DRILLED_ON tract polygon —
    // otherwise tracts whose geometry sits beyond zoom 14 of the surface hole
    // never show up in the viewport.
    useEffect(() => {
        if (!map) return;
        if (!latitude || !longitude) return;
        const points = [];
        const pushIfValid = (lat, lng) => {
            const a = parseFloat(lat);
            const b = parseFloat(lng);
            if (!isNaN(a) && !isNaN(b)) points.push(L.latLng(a, b));
        };
        pushIfValid(latitude, longitude);
        pushIfValid(latitudeBottom, longitudeBottom);
        for (const tp of tractPolygons) {
            for (const ring of tp.positions) {
                for (const [lat, lng] of ring) pushIfValid(lat, lng);
            }
        }
        if (points.length === 0) return;
        map.leafletElement.fitBounds(L.latLngBounds(points), { padding: [30, 30], maxZoom: 16 });
    }, [map, tracts, latitude, longitude, latitudeBottom, longitudeBottom]);

    if (!latitude || !longitude) {
        return (
            <AppMap
                center={[39.8283, -98.5795]}
                zoom={3}
                uploadRef={uploadRef}
                onCoordsExtracted={handleCoordsExtracted}
                mapVisible={false}
                downloadPrefix={name || 'well'}
                placeholder={
                    <div style={{
                        marginTop: 0,
                        marginBottom: 20,
                        padding: 20,
                        backgroundColor: '#f5f5f5',
                        textAlign: 'center',
                        color: '#666',
                    }}>
                        No latitude/longitude data available for this well. Map cannot be displayed.
                    </div>
                }
            />
        );
    }

    const bottomHole = [parseFloat(latitudeBottom), parseFloat(longitudeBottom)];
    const surfaceHole = [parseFloat(latitude), parseFloat(longitude)];
    const polyLine = [surfaceHole, bottomHole];
    const display = latitude === latitudeBottom && longitude === longitudeBottom;

    return (
        <AppMap
            center={surfaceHole}
            zoom={14}
            uploadRef={uploadRef}
            onCoordsExtracted={handleCoordsExtracted}
            onMapReady={setMap}
            downloadPrefix={name || 'well'}
        >
            <DraggableMarker
                position={surfaceHole}
                editMode={editMode}
                onDragEnd={(ll) => onSurfacePositionChange?.(ll)}
            >
                <Popup>
                    <u>Surface Hole Location</u> <br />
                    Well Name: {name} <br />
                    Land ID(s): {land} <br />
                    Latitude: {latitude} <br />
                    Longitude: {longitude}
                </Popup>
            </DraggableMarker>
            {!display && (
                <DraggableMarker
                    position={bottomHole}
                    editMode={editMode}
                    onDragEnd={(ll) => onBottomPositionChange?.(ll)}
                >
                    <Popup>
                        <u>Bottom Hole Location</u> <br />
                        True Vertical Depth: {!props.trueVerticalDepth ? '-' : props.trueVerticalDepth} <br />
                        Total Depth: {!props.totalDepth ? '-' : props.totalDepth} <br />
                        Upper Perf: {!props.upperPerf ? '-' : props.upperPerf} <br />
                        Lower Perf: {!props.lowerPerf ? '-' : props.lowerPerf} <br />
                        Gross Perf: {!props.perfLength ? '-' : props.perfLength} <br />
                        Bottom Latitude: {!props.latitudeBottom ? '-' : props.latitudeBottom} <br />
                        Bottom Longitude: {!props.longitudeBottom ? '-' : props.longitudeBottom} <br />
                    </Popup>
                </DraggableMarker>
            )}
            {!display && <Polyline color='red' positions={polyLine} />}
            <EditablePolygonGroup
                polygons={tractPolygons.map(({ positions, color, tract }) => ({
                    id: tract.id,
                    positions: positions[0],
                    color: getLeafletColor(color),
                    weight: 2,
                    opacity: 0.8,
                    fillOpacity: 0.2,
                    label: <strong>{tract.name || tract.id}</strong>,
                    popup: <TractPopup tract={tract} />,
                }))}
                editMode={editMode}
                allowDraw={editMode && tracts.length > 0}
                onEdit={(tractId, newCoords) => onTractPolygonChange?.(tractId, newCoords)}
                onCreate={(newCoords) => setPendingPolygon(newCoords)}
            />
            {pendingPolygon && (
                <Polygon
                    positions={pendingPolygon}
                    color="#ef6c00"
                    weight={3}
                    opacity={0.95}
                    fillOpacity={0.25}
                    dashArray="6 6"
                />
            )}
            <TractPolygonTargetDialog
                open={!!pendingPolygon}
                tracts={tracts}
                onClose={() => setPendingPolygon(null)}
                onConfirm={(tractId) => {
                    if (onTractPolygonChange) onTractPolygonChange(tractId, pendingPolygon);
                    setPendingPolygon(null);
                }}
            />
        </AppMap>
    );
};

export default WellMapCard;
